import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, Truck, HelpCircle, Calculator, Info, Tag, X, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { isValidZipCode } from '../utils/salesTax';

const CartPage = () => {
  const { 
    cartItems, 
    removeFromCart, 
    updateQuantity, 
    cartTotal,
    discountAmount,
    shippingCost, 
    hasFreeShipping,
    shippingZip,
    updateShippingZip,
    taxCalculation,
    taxAmount,
    orderTotal,
    // Promotions
    couponCode,
    applyCoupon,
    removeCoupon,
    appliedPromos,
    promoMessages,
    unlockMessages,
    promoLoading,
  } = useCart();
  
  const navigate = useNavigate();
  const [zipInput, setZipInput] = useState(shippingZip);
  const [zipError, setZipError] = useState('');
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');

  const handleZipChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipInput(value);
    
    if (value.length === 5) {
      if (isValidZipCode(value)) {
        updateShippingZip(value);
        setZipError('');
      } else {
        setZipError('Invalid ZIP code');
      }
    } else {
      setZipError('');
    }
  };

  const handleCalculateTax = () => {
    if (isValidZipCode(zipInput)) {
      updateShippingZip(zipInput);
      setZipError('');
    } else {
      setZipError('Please enter a valid 5-digit ZIP code');
    }
  };

  const handleApplyCoupon = () => {
    if (!couponInput.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    setCouponError('');
    applyCoupon(couponInput.trim().toUpperCase());
  };

  const handleRemoveCoupon = () => {
    setCouponInput('');
    setCouponError('');
    removeCoupon();
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4 p-12 rounded-xl bg-ivory-100 border border-ivory-300/50 text-center shadow-luxury">
          <div className="w-20 h-20 rounded-xl bg-gold-500/10 flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-10 h-10 text-gold-600" />
          </div>
          <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-3">Your cart is empty</h2>
          <p className="text-ivory-500 mb-6 font-body">Add some products to get started!</p>
          <Button 
            size="lg" 
            asChild 
            className="btn-luxury-primary rounded-lg font-body"
          >
            <Link to="/products" data-testid="browse-products-btn">Browse Products</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory-50">
      {/* Header */}
      <div className="relative gradient-hero py-12 overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gold-500/5 rounded-full blur-[100px]" />
        <div className="container mx-auto px-4 relative z-10">
          <Link 
            to="/products" 
            className="inline-flex items-center gap-2 text-ivory-500 hover:text-gold-600 mb-4 transition-colors font-body"
            data-testid="continue-shopping-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Continue Shopping
          </Link>
          <h1 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800">Shopping Cart</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div 
                key={item.id} 
                className="rounded-xl bg-ivory-100 border border-ivory-300/50 overflow-hidden shadow-luxury"
                data-testid={`cart-item-${item.id}`}
              >
                <div className="p-6">
                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* Product Image */}
                    <div className="w-full sm:w-32 h-32 bg-ivory-200 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Product Info */}
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-heading font-semibold text-lg text-ivory-800 mb-2">{item.name}</h3>
                          <div className="space-y-1 text-sm text-ivory-500 font-body">
                            {/* Dynamically render all selected options */}
                            {item.selectedOptions && Object.entries(item.selectedOptions).map(([key, value]) => {
                              // Skip empty values and internal keys
                              if (!value || key === 'quantity') return null;
                              return (
                                <p key={key}>
                                  {key}: <span className="text-ivory-700">{value}</span>
                                </p>
                              );
                            })}
                            {/* Fallback for legacy items with specific keys */}
                            {(!item.selectedOptions || Object.keys(item.selectedOptions).length === 0) && (
                              <>
                                {item.selectedOptions?.size && <p>Size: <span className="text-ivory-700">{item.selectedOptions.size}</span></p>}
                                {item.selectedOptions?.finish && <p>Finish: <span className="text-ivory-700">{item.selectedOptions.finish}</span></p>}
                              </>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFromCart(item.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          data-testid={`remove-item-${item.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </div>

                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 border-ivory-300 bg-transparent text-ivory-700 hover:bg-ivory-200 hover:border-gold-500/50 rounded-lg"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            data-testid={`decrease-qty-${item.id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="text-ivory-800 font-semibold w-12 text-center font-body">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 border-ivory-300 bg-transparent text-ivory-700 hover:bg-ivory-200 hover:border-gold-500/50 rounded-lg"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            data-testid={`increase-qty-${item.id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gold-600 font-body">${item.total.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              {/* Tax Calculator */}
              <div className="rounded-xl bg-ivory-100 border border-ivory-300/50 overflow-hidden shadow-luxury">
                <div className="p-4 border-b border-ivory-200 bg-gold-500/5">
                  <h3 className="font-heading font-semibold text-ivory-800 flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-gold-600" />
                    Estimate Tax
                  </h3>
                </div>
                <div className="p-4">
                  <Label htmlFor="zip-estimate" className="text-ivory-600 text-sm font-body mb-2 block">
                    Enter your ZIP code
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="zip-estimate"
                      value={zipInput}
                      onChange={handleZipChange}
                      placeholder="92705"
                      maxLength={5}
                      className={`bg-ivory-50 border-ivory-300 text-ivory-800 font-body ${zipError ? 'border-red-500' : ''}`}
                      data-testid="zip-estimate-input"
                    />
                    <Button 
                      onClick={handleCalculateTax}
                      className="btn-luxury-primary font-body px-4"
                      data-testid="calculate-tax-btn"
                    >
                      Calculate
                    </Button>
                  </div>
                  {zipError && (
                    <p className="text-red-500 text-xs mt-2 font-body">{zipError}</p>
                  )}
                  {taxCalculation.location && (
                    <p className="text-gold-700 text-xs mt-2 font-body">
                      Tax rate for {taxCalculation.location.city}: {taxCalculation.taxRateDisplay}
                    </p>
                  )}
                  {taxCalculation.isOutOfState && (
                    <p className="text-green-600 text-xs mt-2 font-body">
                      No sales tax for out-of-state orders
                    </p>
                  )}
                </div>
              </div>
              
              {/* Coupon Code */}
              <div className="rounded-xl bg-ivory-100 border border-ivory-300/50 overflow-hidden shadow-luxury">
                <div className="p-4 border-b border-ivory-200 bg-gold-500/5">
                  <h3 className="font-heading font-semibold text-ivory-800 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-gold-600" />
                    Coupon Code
                  </h3>
                </div>
                <div className="p-4">
                  {couponCode && appliedPromos.some(p => p.type === 'discount_code') ? (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-mono font-bold text-green-700">{couponCode}</span>
                        <span className="text-sm text-green-600">applied</span>
                      </div>
                      <button 
                        onClick={handleRemoveCoupon}
                        className="text-red-500 hover:text-red-600"
                        data-testid="remove-coupon-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <Input
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          className="bg-ivory-50 border-ivory-300 text-ivory-800 font-mono uppercase"
                          data-testid="coupon-input"
                        />
                        <Button 
                          onClick={handleApplyCoupon}
                          className="btn-luxury-primary font-body px-4"
                          disabled={promoLoading}
                          data-testid="apply-coupon-btn"
                        >
                          Apply
                        </Button>
                      </div>
                      {couponError && (
                        <p className="text-red-500 text-xs mt-2 font-body">{couponError}</p>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {/* Order Summary Card */}
              <div className="rounded-xl bg-ivory-100 border border-ivory-300/50 overflow-hidden shadow-luxury">
                <div className="p-6 border-b border-ivory-200">
                  <h2 className="text-xl font-heading font-semibold text-ivory-800">Order Summary</h2>
                </div>
                <div className="p-6 space-y-4">
                  {/* Promo Messages */}
                  {promoMessages.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {promoMessages.map((msg, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                            msg.type === 'success' 
                              ? 'bg-green-50 border border-green-200 text-green-700'
                              : 'bg-blue-50 border border-blue-200 text-blue-700'
                          }`}
                        >
                          <span>{msg.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Unlock Messages */}
                  {unlockMessages.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {unlockMessages.map((msg, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm animate-pulse"
                        >
                          <Truck className="w-4 h-4" />
                          <span>{msg.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="space-y-3 font-body">
                    <div className="flex justify-between text-ivory-500">
                      <span>Subtotal ({cartItems.length} items)</span>
                      <span className="font-semibold text-ivory-800">${cartTotal.toFixed(2)}</span>
                    </div>
                    
                    {/* Discount */}
                    {discountAmount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount</span>
                        <span className="font-semibold">-${discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-ivory-500">
                      <span>Shipping</span>
                      {hasFreeShipping ? (
                        <span className="font-semibold text-green-600">FREE</span>
                      ) : (
                        <span className="font-semibold text-ivory-800">${shippingCost.toFixed(2)}</span>
                      )}
                    </div>
                    
                    {hasFreeShipping && (
                      <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
                        <Truck className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <p className="text-sm text-green-700 font-medium">
                          You&apos;ve qualified for FREE shipping!
                        </p>
                      </div>
                    )}
                    
                    {/* Tax Line */}
                    <div className="flex justify-between text-ivory-500">
                      <div className="flex items-center gap-1">
                        <span>Estimated Tax</span>
                        {taxCalculation.taxRate > 0 && (
                          <span className="text-xs text-ivory-400">({taxCalculation.taxRateDisplay})</span>
                        )}
                      </div>
                      <span className="font-semibold text-ivory-800" data-testid="estimated-tax">
                        ${taxAmount.toFixed(2)}
                      </span>
                    </div>
                    
                    {!shippingZip && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-ivory-200/50 border border-ivory-300">
                        <Info className="w-4 h-4 text-ivory-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-ivory-500">
                          Enter your ZIP code above to see accurate tax estimate
                        </p>
                      </div>
                    )}
                    
                    <div className="border-t border-ivory-200 pt-4 mt-4">
                      <div className="flex justify-between">
                        <span className="font-semibold text-ivory-800">Estimated Total</span>
                        <span className="font-bold text-gold-600 text-2xl" data-testid="cart-total">
                          ${orderTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Button
                    size="lg"
                    className="w-full text-lg py-6 btn-luxury-primary rounded-lg font-body"
                    onClick={() => navigate('/checkout')}
                    data-testid="checkout-btn"
                  >
                    Proceed to Checkout
                  </Button>

                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full btn-luxury-outline rounded-lg font-body"
                    asChild
                  >
                    <Link to="/products">Continue Shopping</Link>
                  </Button>

                  <div className="flex items-start gap-3 p-4 rounded-lg bg-ivory-50 border border-ivory-200 mt-4">
                    <HelpCircle className="w-5 h-5 text-ivory-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-ivory-500 font-body">
                      <span className="font-medium text-ivory-600">Need help?</span> Contact our customer support team for assistance with your order.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
